import React, { useState, useEffect } from 'react';
import '../styles/App.css';

const Navbar = ({ setCurrentPage, currentPage }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeItem, setActiveItem] = useState('home');
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setActiveItem(currentPage);
  }, [currentPage]);

  const navItems = [
    { id: 'home', label: 'Home', icon: 'fas fa-home' },
    { id: 'about', label: 'About', icon: 'fas fa-info-circle' },
    { id: 'training', label: 'Training', icon: 'fas fa-chalkboard-user' },
    { id: 'services', label: 'Services', icon: 'fas fa-cogs' },
    { id: 'products', label: 'Products', icon: 'fas fa-box' },
    { id: 'team', label: 'Our Team', icon: 'fas fa-users' },
    { id: 'contact', label: 'Contact', icon: 'fas fa-envelope' }
  ];

  const changePage = (page) => {
    setCurrentPage(page);
    setActiveItem(page);
    setMobileOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          {/* Logo Section with Image */}
          <div className="logo" onClick={() => changePage('home')}>
            {/* Image Logo - Place your logo image in public folder */}
            {!imageError ? (
              <img 
                src="/vtech-logo.png" 
                alt="VTECH CODERS Logo" 
                className="logo-image"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="logo-icon">
                <i className="fas fa-server"></i>
              </div>
            )}
            
            <div className="logo-text">
              <h2>VTECH CODERS</h2>
              <p><i className="fas fa-microchip"></i> We Code Your Ideas</p>
            </div>
          </div>
          
          {/* Mobile Menu Button */}
          <div className={`mobile-menu-btn ${mobileOpen ? 'active' : ''}`} onClick={() => setMobileOpen(!mobileOpen)}>
            <div className="menu-icon">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
          
          {/* Navigation Links */}
          <ul className={`nav-links ${mobileOpen ? 'open' : ''}`}>
            {navItems.map(item => (
              <li
                key={item.id}
                onClick={() => changePage(item.id)}
                className={`nav-item ${activeItem === item.id ? 'active' : ''}`}
              >
                <i className={item.icon}></i>
                <span>{item.label}</span>
                <div className="nav-indicator"></div>
              </li>
            ))}
          </ul>
        </div>
      </nav>
      
      <style jsx>{`
        /* Navbar Styles */
        .navbar {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          transition: all 0.4s cubic-bezier(0.2, 0.9, 0.4, 1.1);
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(10px);
          box-shadow: 0 2px 20px rgba(0, 0, 0, 0.05);
        }
        
        .navbar.scrolled {
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(12px);
          box-shadow: 0 4px 25px rgba(0, 0, 0, 0.08);
        }
        
        .nav-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.8rem 2rem;
          max-width: 1400px;
          margin: 0 auto;
          position: relative;
        }
        
        /* Logo Styles */
        .logo {
          display: flex;
          align-items: center;
          gap: 12px;
          cursor: pointer;
          position: relative;
          z-index: 1001;
        }
        
        /* Image Logo Styles */
        .logo-image {
          width: 50px;
          height: 50px;
          border-radius: 12px;
          object-fit: cover;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(59, 130, 246, 0.2);
          animation: fadeIn 0.5s ease;
        }
        
        .logo:hover .logo-image {
          transform: scale(1.05) rotate(2deg);
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
        }
        
        /* Fallback Icon Styles */
        .logo-icon {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
          transition: all 0.3s ease;
          animation: pulse 2s ease-in-out infinite;
        }
        
        .logo-icon i {
          font-size: 28px;
          color: white;
          transition: transform 0.3s ease;
        }
        
        .logo:hover .logo-icon {
          transform: scale(1.05);
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.5);
        }
        
        .logo:hover .logo-icon i {
          transform: rotate(5deg);
        }
        
        .logo-text h2 {
          font-weight: 800;
          background: linear-gradient(135deg, #3b82f6, #8b5cf6);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          font-size: 1.6rem;
          margin: 0;
          line-height: 1.2;
          transition: transform 0.3s ease;
        }
        
        .logo:hover .logo-text h2 {
          transform: translateX(3px);
        }
        
        .logo-text p {
          font-size: 0.7rem;
          margin: 0;
          color: #64748b;
          letter-spacing: 0.5px;
        }
        
        /* Mobile Menu Button */
        .mobile-menu-btn {
          display: none;
          cursor: pointer;
          z-index: 1001;
          padding: 10px;
        }
        
        .menu-icon {
          width: 30px;
          height: 20px;
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }
        
        .menu-icon span {
          width: 100%;
          height: 2px;
          background: linear-gradient(95deg, #3b82f6, #8b5cf6);
          border-radius: 2px;
          transition: all 0.3s ease;
        }
        
        .mobile-menu-btn.active .menu-icon span:first-child {
          transform: rotate(45deg) translate(6px, 6px);
        }
        
        .mobile-menu-btn.active .menu-icon span:nth-child(2) {
          opacity: 0;
          transform: translateX(-10px);
        }
        
        .mobile-menu-btn.active .menu-icon span:last-child {
          transform: rotate(-45deg) translate(6px, -6px);
        }
        
        /* Navigation Links */
        .nav-links {
          display: flex;
          gap: 2rem;
          list-style: none;
          margin: 0;
          padding: 0;
          transition: all 0.4s ease;
        }
        
        .nav-item {
          cursor: pointer;
          font-weight: 500;
          transition: all 0.3s ease;
          padding: 0.5rem 0;
          position: relative;
          display: flex;
          align-items: center;
          gap: 8px;
          color: #1e293b;
        }
        
        .nav-item i {
          font-size: 1rem;
          transition: transform 0.3s ease;
        }
        
        .nav-item span {
          position: relative;
        }
        
        .nav-indicator {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 0;
          height: 2px;
          background: linear-gradient(95deg, #3b82f6, #8b5cf6);
          transition: width 0.3s ease;
        }
        
        .nav-item:hover {
          color: #3b82f6;
        }
        
        .nav-item:hover i {
          transform: translateY(-2px);
        }
        
        .nav-item:hover .nav-indicator {
          width: 100%;
        }
        
        .nav-item.active {
          color: #3b82f6;
        }
        
        .nav-item.active .nav-indicator {
          width: 100%;
        }
        
        /* Animations */
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
          }
          50% {
            transform: scale(1.05);
            box-shadow: 0 6px 20px rgba(59, 130, 246, 0.5);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Mobile Responsive Styles */
        @media (max-width: 850px) {
          .nav-container {
            padding: 0.8rem 1.5rem;
          }
          
          .logo-image {
            width: 40px;
            height: 40px;
          }
          
          .logo-icon {
            width: 40px;
            height: 40px;
          }
          
          .logo-icon i {
            font-size: 22px;
          }
          
          .logo-text h2 {
            font-size: 1.3rem;
          }
          
          .logo-text p {
            font-size: 0.6rem;
          }
          
          .mobile-menu-btn {
            display: block;
          }
          
          .nav-links {
            position: fixed;
            top: 0;
            right: -100%;
            width: 80%;
            max-width: 320px;
            height: 100vh;
            background: rgba(255, 255, 255, 0.98);
            backdrop-filter: blur(20px);
            flex-direction: column;
            justify-content: center;
            gap: 1.8rem;
            padding: 2rem;
            box-shadow: -5px 0 30px rgba(0, 0, 0, 0.1);
            transition: right 0.4s cubic-bezier(0.2, 0.9, 0.4, 1.1);
            z-index: 1000;
          }
          
          .nav-links.open {
            right: 0;
            animation: slideIn 0.4s ease;
          }
          
          .nav-item {
            padding: 0.8rem;
            font-size: 1.1rem;
            justify-content: flex-start;
            width: 100%;
            border-radius: 12px;
            transition: all 0.3s ease;
          }
          
          .nav-item i {
            font-size: 1.2rem;
            width: 30px;
          }
          
          .nav-item:hover {
            background: linear-gradient(95deg, rgba(59, 130, 246, 0.1), rgba(139, 92, 246, 0.1));
            transform: translateX(8px);
          }
          
          .nav-item.active {
            background: linear-gradient(95deg, rgba(59, 130, 246, 0.15), rgba(139, 92, 246, 0.15));
          }
          
          .nav-indicator {
            display: none;
          }
        }
        
        /* Tablet Responsive */
        @media (min-width: 851px) and (max-width: 1024px) {
          .nav-links {
            gap: 1.5rem;
          }
          
          .nav-item {
            font-size: 0.95rem;
          }
          
          .logo-text h2 {
            font-size: 1.4rem;
          }
          
          .logo-image {
            width: 45px;
            height: 45px;
          }
        }
        
        /* Small Mobile Devices */
        @media (max-width: 480px) {
          .nav-container {
            padding: 0.6rem 1rem;
          }
          
          .logo-image {
            width: 35px;
            height: 35px;
          }
          
          .logo-icon {
            width: 35px;
            height: 35px;
          }
          
          .logo-icon i {
            font-size: 18px;
          }
          
          .logo-text h2 {
            font-size: 1.1rem;
          }
          
          .logo-text p {
            font-size: 0.5rem;
          }
          
          .nav-links {
            width: 100%;
            max-width: 100%;
            padding: 1.5rem;
          }
          
          .nav-item {
            padding: 0.7rem;
            font-size: 1rem;
          }
          
          .nav-item i {
            font-size: 1rem;
          }
        }
        
        /* Dark Mode Support */
        @media (prefers-color-scheme: dark) {
          .navbar {
            background: rgba(15, 23, 42, 0.95);
          }
          
          .navbar.scrolled {
            background: rgba(15, 23, 42, 0.98);
          }
          
          .nav-item {
            color: #e2e8f0;
          }
          
          .logo-text p {
            color: #94a3b8;
          }
          
          .nav-links {
            background: rgba(15, 23, 42, 0.98);
          }
          
          .nav-item:hover {
            background: linear-gradient(95deg, rgba(59, 130, 246, 0.2), rgba(139, 92, 246, 0.2));
          }
          
          .nav-item.active {
            background: linear-gradient(95deg, rgba(59, 130, 246, 0.25), rgba(139, 92, 246, 0.25));
          }
        }
      `}</style>
    </>
  );
};

export default Navbar;